import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react';
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useLayout } from "./LayoutContext";
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import PropTypes from 'prop-types';

// Importar tours específicos
import { getLayoutTour } from '../lib/tour/layoutTours';
import { getDashboardTour } from '../lib/tour/dashboardTour';
import { getVehicleTourByPath, createVehicleProfileTour } from '../lib/tour/vehicleTours';
import { getPersonsTourByPath, createPersonProfileTour } from '../lib/tour/personsTours';
import { getManifestTourByPath } from '../lib/tour/manifestTours';
import { createHumanResourceTour } from '../lib/tour/humanResourceTours';
import { createVehicleDocumentsTour } from '../lib/tour/vehicleDocumentsTours';

const TourContext = createContext();

export const TourProvider = ({ children }) => {
  const { theme } = useLayout(); 
  const [driverInstance, setDriverInstance] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [currentTour, setCurrentTour] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef(null);

  // Permisos de edición según rol
  const { selectedRole: RoleSelectedByUser } = useAuth();
  const canEdit = RoleSelectedByUser?.id !== 11;
  const canEditPersons = RoleSelectedByUser?.id !== 12;
  
  // Inicializar driver con configuración del tema
  useEffect(() => {
    const newDriverInstance = driver({
      animate: true,
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      stagePadding: 10,
      popoverClass: `GM__${theme}-driver-popover`,
      onDestroyed: () => {
        setIsActive(false);
        setCurrentTour(null);
      },
      onDeselected: () => {
        setIsActive(false);
        setCurrentTour(null);
      }
    });
    
    setDriverInstance(newDriverInstance);
    driverRef.current = newDriverInstance;
    
    // Cleanup al desmontar
    return () => {
      if (newDriverInstance) {
        newDriverInstance.destroy();
      }
    };
  }, [theme]);

  // Función para obtener tours por tipo
  const getTourSteps = useCallback((tourType) => {
    const tourMap = {
      // Tours del layout (header, leftNav, etc.)
      header: () => getLayoutTour(theme).header,
      leftNav: () => getLayoutTour(theme).leftNav,
      
      // Tours de páginas específicas
      dashboard: () => getDashboardTour(theme),
      currentPage: () => {
        // Detectar página actual y devolver tour correspondiente
        // Se usa useLocation (react-router-dom) para que funcione con HashRouter
        // IMPORTANTE: /manifest-control/guide-dashboard contiene 'dashboard',
        // por eso este bloque debe ir ANTES del check genérico de 'dashboard'.
        const pathname = location.pathname;
        if (pathname.startsWith('/manifest-control')) {
          return getManifestTourByPath(theme, pathname, driverRef);
        }
        if (pathname === '/alerts-persons') {
          return createHumanResourceTour(theme, driverRef);
        }
        if (pathname === '/alerts-vehicles') {
          return createVehicleDocumentsTour(theme, driverRef);
        }
        if (pathname.includes('dashboard')) return getDashboardTour(theme);
        if (pathname.startsWith('/vehicles')) {
          // Ruta de perfil individual: /vehicles/:id (excluye /add y /setting)
          if (/^\/vehicles\/[^/]+/.test(pathname) && !pathname.includes('add') && !pathname.includes('setting')) {
            return createVehicleProfileTour(theme, driverRef, canEdit);
          }
          return getVehicleTourByPath(theme, pathname);
        }
        if (pathname.startsWith('/persons')) {
          if (/^\/persons\/profile\/[^/]+/.test(pathname)) {
            return createPersonProfileTour(theme, driverRef, canEditPersons);
          }
          return getPersonsTourByPath(theme, pathname);
        }
        // Agregar más páginas según necesites
        return getLayoutTour(theme).general;
      }
    };

    return tourMap[tourType] ? tourMap[tourType]() : null;
  }, [theme, location.pathname, canEdit, canEditPersons]);

  // Función principal para iniciar tours
  const startTour = useCallback((tourType) => {
    if (!driverInstance) {
      console.warn('Driver instance no está disponible');
      return;
    }

    // Manejar caso especial de helpSection
    if (tourType === 'helpSection') {
      navigate('/help');
      return;
    }

    // Obtener steps del tour
    const tourSteps = getTourSteps(tourType);
    
    if (!tourSteps || tourSteps.length === 0) {
      console.warn(`Tour "${tourType}" no encontrado o vacío`);
      return;
    }

    // Configurar estado
    setIsActive(true);
    setCurrentTour(tourType);

    // Pequeño delay para asegurar DOM renderizado
    setTimeout(() => {
      try {
        driverInstance.setSteps(tourSteps);
        driverInstance.drive();
      } catch (error) {
        console.error('Error iniciando tour:', error);
        setIsActive(false);
        setCurrentTour(null);
      }
    }, 100);
  }, [driverInstance, getTourSteps, navigate]);

  // Función para detener tour activo
  const stopTour = useCallback(() => {
    if (driverInstance && isActive) {
      driverInstance.destroy();
      setIsActive(false);
      setCurrentTour(null);
    }
  }, [driverInstance, isActive]);

  // Secuencia de onboarding completa
  const startOnboardingSequence = useCallback(() => {
    const sequence = ['header', 'leftNav', 'currentPage'];
    let currentIndex = 0;

    const runNextTour = () => {
      if (currentIndex < sequence.length) {
        const tourType = sequence[currentIndex];
        currentIndex++;
        
        // Configurar callback para continuar secuencia
        const originalOnDestroy = driverInstance?.options?.onDestroyed;
        
        if (driverInstance) {
          driverInstance.setConfig({
            ...driverInstance.options,
            onDestroyed: () => {
              if (originalOnDestroy) originalOnDestroy();
              setTimeout(runNextTour, 1000);
            }
          });
        }
        
        startTour(tourType);
      }
    };

    runNextTour();
  }, [driverInstance, startTour]);

  const contextValue = {
    // Estado
    isActive,
    currentTour,
    
    // Acciones principales
    startTour,
    stopTour,
    startOnboardingSequence,
    
    // Utilidades
    driverInstance
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour debe ser usado dentro de un TourProvider');
  }
  return context;
};

TourProvider.propTypes = {
  children: PropTypes.node.isRequired,
};